import React from 'react';
import { Helmet } from 'react-helmet-async';

const SITE_NAME = 'OpenUse';
const SITE_URL = 'https://openuse.io';
const DEFAULT_IMAGE = `${SITE_URL}/images/brand/logo.png`;

interface SEOProps {
  /** Page-specific title. Site name is appended automatically. */
  title: string;
  /** Concise, keyword-rich summary shown in search results (~150-160 chars). */
  description: string;
  /** Path beginning with "/", e.g. "/product". Used to build the canonical URL. */
  path: string;
  /** Absolute image URL for social sharing previews. Defaults to the site logo. */
  image?: string;
  /** Optional JSON-LD structured data object(s) to embed for this page. */
  structuredData?: Record<string, unknown> | Record<string, unknown>[];
  /** Set true for pages that should not appear in search results (e.g. thank-you pages). */
  noindex?: boolean;
}

/**
 * Injects per-page <title>, meta description, canonical link, Open Graph /
 * Twitter Card tags, and optional JSON-LD structured data. Keeping this in
 * one place ensures every route is indexable and shareable instead of all
 * pages sharing the single static title in public/index.html.
 */
const SEO: React.FC<SEOProps> = ({ title, description, path, image, structuredData, noindex }) => {
  const fullTitle = `${title} | ${SITE_NAME}`;
  const canonicalUrl = `${SITE_URL}${path}`;
  const ogImage = image ?? DEFAULT_IMAGE;
  const jsonLdItems = structuredData
    ? Array.isArray(structuredData)
      ? structuredData
      : [structuredData]
    : [];

  return (
    <Helmet>
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={canonicalUrl} />
      <meta name="robots" content={noindex ? 'noindex, nofollow' : 'index, follow'} />

      <meta property="og:type" content="website" />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:image" content={ogImage} />

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={ogImage} />

      {jsonLdItems.map((item, idx) => (
        <script key={idx} type="application/ld+json">
          {JSON.stringify(item)}
        </script>
      ))}
    </Helmet>
  );
};

export default SEO;
export { SITE_URL, SITE_NAME };
